import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  TrendingUp, TrendingDown, Activity, PackageMinus, AlertTriangle, Loader2, Boxes, Factory,
} from "lucide-react";

const PALETTE = ["#F59E0B", "#38BDF8", "#34D399", "#F472B6", "#A78BFA", "#FB7185", "#FACC15", "#4ADE80"];
const AMBER = "#F59E0B";

const nf = (n) => (n === null || n === undefined ? "—" : new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(n));
const shortDate = (iso) => { const [, m, d] = iso.split("-"); return `${d}/${m}`; };

function ChartCard({ title, subtitle, icon: Icon, children, testid }) {
  return (
    <Card className="bg-card/70 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-primary" />}
        <div>
          <div className="font-display text-base sm:text-lg font-bold uppercase leading-none">{title}</div>
          {subtitle && <div className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {children}
    </Card>
  );
}

const tooltipStyle = {
  contentStyle: { background: "#0B0F17", border: "1px solid #1f2937", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#9CA3AF" }, itemStyle: { color: "#E5E7EB" },
};

export default function GestaoConsumo() {
  const [dias, setDias] = useState("30");
  const [unitSel, setUnitSel] = useState("__todas");
  const [units, setUnits] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/units").then((r) => setUnits(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = `?dias=${dias}${unitSel !== "__todas" ? `&unit_id=${unitSel}` : ""}`;
      const r = await api.get(`/gestao/consumo${qs}`);
      setData(r.data);
    } finally { setLoading(false); }
  }, [dias, unitSel]);
  useEffect(() => { load(); }, [load]);

  const resumo = data?.resumo;
  const variacao = resumo?.variacao_pct;
  const varUp = typeof variacao === "number" && variacao >= 0;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="gestao-consumo-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] sm:text-xs text-primary uppercase tracking-[0.18em] font-bold">Gestão</div>
          <h1 className="font-display text-2xl sm:text-4xl font-extrabold uppercase">Consumo & Abastecimento</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dias} onValueChange={setDias}>
            <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="gestao-periodo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Select value={unitSel} onValueChange={setUnitSel}>
            <SelectTrigger className="h-9 w-[190px] text-xs" data-testid="gestao-unidade"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__todas">Todas as unidades</SelectItem>
              {units.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`gestao-unit-${u.codigo}`}>{u.codigo} — {u.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="bg-card/70 border border-primary/40 p-4" data-testid="gestao-kpi-consumo">
              <div className="flex items-center justify-between"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Consumo total</div><PackageMinus className="w-4 h-4 text-primary" /></div>
              <div className="font-display text-2xl sm:text-3xl font-extrabold text-primary mt-1">{nf(resumo?.consumo_total)}</div>
              <div className="text-[11px] text-muted-foreground">{resumo?.num_saidas || 0} saídas · {data?.dias}d</div>
            </Card>
            <Card className="bg-card/70 border border-sky-600/40 p-4" data-testid="gestao-kpi-media">
              <div className="flex items-center justify-between"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Média diária</div><Activity className="w-4 h-4 text-sky-300" /></div>
              <div className="font-display text-2xl sm:text-3xl font-extrabold text-sky-300 mt-1">{nf(resumo?.media_diaria)}</div>
              <div className="text-[11px] text-muted-foreground">un/dia no período</div>
            </Card>
            <Card className="bg-card/70 border border-emerald-600/40 p-4" data-testid="gestao-kpi-variacao">
              <div className="flex items-center justify-between"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Variação vs. anterior</div>{varUp ? <TrendingUp className="w-4 h-4 text-emerald-300" /> : <TrendingDown className="w-4 h-4 text-rose-300" />}</div>
              <div className={`font-display text-2xl sm:text-3xl font-extrabold mt-1 ${variacao === null || variacao === undefined ? "text-muted-foreground/60" : varUp ? "text-emerald-300" : "text-rose-300"}`}>
                {variacao === null || variacao === undefined ? "—" : `${varUp ? "+" : ""}${nf(variacao)}%`}
              </div>
              <div className="text-[11px] text-muted-foreground">ant.: {nf(resumo?.consumo_anterior)}</div>
            </Card>
            <Card className="bg-card/70 border border-amber-600/40 p-4" data-testid="gestao-kpi-risco">
              <div className="flex items-center justify-between"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">SKUs em risco (≤7d)</div><AlertTriangle className="w-4 h-4 text-amber-300" /></div>
              <div className="font-display text-2xl sm:text-3xl font-extrabold text-amber-300 mt-1">{resumo?.skus_risco ?? 0}</div>
              <div className="text-[11px] text-muted-foreground">ruptura prevista</div>
            </Card>
          </div>

          {/* Evolução */}
          <ChartCard title="Evolução do consumo" subtitle="Total consumido por dia" icon={Activity} testid="gestao-chart-evolucao">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data?.evolucao || []} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs><linearGradient id="gConsumo" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={AMBER} stopOpacity={0.5} /><stop offset="100%" stopColor={AMBER} stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="data" tickFormatter={shortDate} tick={{ fill: "#9CA3AF", fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} labelFormatter={shortDate} formatter={(v) => [nf(v), "Consumo"]} />
                <Area type="monotone" dataKey="total" stroke={AMBER} strokeWidth={2} fill="url(#gConsumo)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Consumo por unidade */}
            <ChartCard title="Consumo por unidade" subtitle="Ranking no período" icon={Factory} testid="gestao-chart-unidade">
              {(data?.por_unidade || []).length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={Math.max(200, (data.por_unidade.length) * 38)}>
                  <BarChart data={data.por_unidade} layout="vertical" margin={{ top: 5, right: 16, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "#9CA3AF", fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="codigo" tick={{ fill: "#E5E7EB", fontSize: 11 }} width={54} />
                    <Tooltip {...tooltipStyle} formatter={(v, _n, p) => [nf(v), p?.payload?.unidade]} />
                    <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                      {data.por_unidade.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Consumo por categoria */}
            <ChartCard title="Consumo por categoria" subtitle="Participação no total" icon={Boxes} testid="gestao-chart-categoria">
              {(data?.por_categoria || []).length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={data.por_categoria} dataKey="total" nameKey="categoria" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                      {data.por_categoria.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip {...tooltipStyle} formatter={(v, n) => [nf(v), n]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Por motivo */}
            <ChartCard title="Consumo por motivo" subtitle="Venda / Uso / Produção / Perda / Outro" icon={PackageMinus} testid="gestao-chart-tipo">
              {(data?.por_tipo || []).length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.por_tipo} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                    <XAxis dataKey="tipo_label" tick={{ fill: "#9CA3AF", fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} allowDecimals={false} />
                    <Tooltip {...tooltipStyle} formatter={(v) => [nf(v), "Consumo"]} />
                    <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                      {data.por_tipo.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Top produtos */}
            <ChartCard title="Top produtos consumidos" subtitle="Maiores volumes no período" icon={TrendingUp} testid="gestao-chart-top">
              {(data?.top_produtos || []).length === 0 ? <Empty /> : (
                <div className="divide-y divide-border" data-testid="gestao-top-list">
                  {data.top_produtos.map((p, i) => (
                    <div key={p.sku + i} className="flex items-center justify-between gap-3 py-2" data-testid={`gestao-top-${p.sku}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-6 h-6 rounded-sm bg-primary/15 text-primary font-mono text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                        <div className="min-w-0">
                          <div className="text-sm truncate">{p.produto}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">{p.sku} · média {nf(p.media_diaria)}/dia</div>
                        </div>
                      </div>
                      <div className="font-mono font-bold text-primary shrink-0">{nf(p.total)}</div>
                    </div>
                  ))}
                </div>
              )}
            </ChartCard>
          </div>

          {/* Risco de ruptura */}
          <ChartCard title="Risco de ruptura" subtitle="Cobertura de estoque estimada pela média de consumo" icon={AlertTriangle} testid="gestao-risco">
            {(data?.risco_ruptura || []).length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Sem consumo no período para estimar cobertura.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left p-2">Produto</th>
                      <th className="text-left p-2">Unidade</th>
                      <th className="text-right p-2">Disponível</th>
                      <th className="text-right p-2">Média/dia</th>
                      <th className="text-right p-2">Cobertura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.risco_ruptura.map((r, i) => {
                      const crit = r.dias_restantes <= 3, warn = r.dias_restantes <= 7;
                      return (
                        <tr key={r.sku + r.unidade_codigo + i} className="border-b border-border/60 hover:bg-secondary/30" data-testid={`gestao-risco-${r.sku}-${r.unidade_codigo}`}>
                          <td className="p-2"><div className="font-medium">{r.produto}</div><div className="text-[10px] font-mono text-muted-foreground">{r.sku}</div></td>
                          <td className="p-2 font-mono text-xs">{r.unidade_codigo}</td>
                          <td className="p-2 text-right font-mono">{nf(r.disponivel)}</td>
                          <td className="p-2 text-right font-mono text-muted-foreground">{nf(r.media_diaria)}</td>
                          <td className="p-2 text-right">
                            <span className={`inline-flex rounded-sm border px-2 py-0.5 text-xs font-bold font-mono ${crit ? "text-rose-300 border-rose-600/50 bg-rose-950/40" : warn ? "text-amber-300 border-amber-600/50 bg-amber-950/40" : "text-emerald-300 border-emerald-600/40 bg-emerald-950/30"}`}>
                              {r.dias_restantes <= 0 ? "0d" : `${nf(r.dias_restantes)}d`}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </>
      )}
    </div>
  );
}

function Empty() {
  return <div className="py-10 text-center text-sm text-muted-foreground">Sem dados no período.</div>;
}
